import { SIZES } from '../firebase/schema';

/** Size column sets keyed by grid type (aligned with firebase/schema SIZES). */
export const SIZE_GRID_COLUMNS = {
  ADULT: [...SIZES.ADULT],
  ADULT_EXTENDED: [...SIZES.ADULT_EXTENDED],
  YOUTH: [...SIZES.YOUTH],
  TODDLER: [...SIZES.TODDLER],
  INFANT: [...SIZES.INFANT],
};

export const SIZE_GRID_ORDER = ['ADULT', 'ADULT_EXTENDED', 'YOUTH', 'TODDLER', 'INFANT'];

export const SIZE_GRID_LABELS = {
  ADULT: 'Adult Sizes',
  ADULT_EXTENDED: 'Adult Extended Sizes (4XL+)',
  YOUTH: 'Youth Sizes',
  TODDLER: 'Toddler Sizes',
  INFANT: 'Infant Sizes',
};

/** Short labels for table section headers. */
export const SIZE_GRID_HEADER_LABELS = {
  ADULT: 'ADULT SIZES',
  ADULT_EXTENDED: 'ADULT EXTENDED SIZES',
  YOUTH: 'YOUTH SIZES',
  TODDLER: 'TODDLER SIZES',
  INFANT: 'INFANT SIZES',
};

export const MAX_SIZE_COLUMNS = Math.max(
  ...SIZE_GRID_ORDER.map((key) => SIZE_GRID_COLUMNS[key].length)
);

/** Firestore / forms often store size qty as strings. */
export const toQuantity = (value) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? 0 : n;
};

export const extractStyleNo = (garmentId) => {
  if (!garmentId) return '';
  const parts = String(garmentId).split(' - ');
  return parts[0].trim();
};

/** Detect which size grid a line belongs to from its size breakdown. */
export const detectSizeGrid = (sizes) => {
  if (!sizes) return 'ADULT';
  const sizeKeys = Object.keys(sizes).filter((key) => toQuantity(sizes[key]) > 0);

  if (sizeKeys.some((size) => SIZES.ADULT_EXTENDED.includes(size))) return 'ADULT_EXTENDED';
  if (sizeKeys.some((size) => String(size).startsWith('Y'))) return 'YOUTH';
  if (sizeKeys.some((size) => String(size).includes('T') && /\d/.test(size))) return 'TODDLER';
  if (sizeKeys.some((size) => SIZES.INFANT.includes(size))) return 'INFANT';

  return 'ADULT';
};

export const lineTotalPieces = (line) =>
  Object.values(line?.sizes || {}).reduce((sum, qty) => sum + toQuantity(qty), 0);

/**
 * Piece count for COGS. Prefer totalQuantity when positive; otherwise sum sizes.
 * External writers sometimes leave totalQuantity at 0 while sizes are populated.
 */
export const lineCogsQuantity = (line) => {
  const fromTotal =
    line?.totalQuantity != null && line?.totalQuantity !== ''
      ? toQuantity(line.totalQuantity)
      : 0;
  if (fromTotal > 0) return fromTotal;
  return lineTotalPieces(line);
};

/** Per-piece COGS from the line, falling back to the product catalog. */
export const lineCogsPerPiece = (line, products = []) => {
  const fromLine = Number(line?.cogs);
  if (fromLine > 0) return fromLine;
  const styleNo = extractStyleNo(line?.garmentId);
  const product = products.find((p) => p.styleNo === styleNo);
  return Number(product?.cogs) || 0;
};

/**
 * Total garment COGS for a line (qty × per-piece COGS).
 * Prefer stored totalLineCOGS when positive. If it is 0/missing but unit cogs
 * and qty exist (common for external integrations), recalculate.
 */
export const lineCogsTotal = (line, products = []) => {
  const qty = lineCogsQuantity(line);
  const computed = qty * lineCogsPerPiece(line, products);

  const stored = line?.totalLineCOGS;
  if (stored != null && stored !== '' && !Number.isNaN(Number(stored))) {
    const storedNum = Number(stored);
    if (storedNum === 0 && computed > 0) return computed;
    return storedNum;
  }

  return computed;
};

export const orderCogsTotal = (lines, products = []) =>
  (lines || []).reduce((sum, line) => sum + lineCogsTotal(line, products), 0);

/**
 * Build aggregated purchase-order rows grouped by size grid type.
 * Combines lines with the same style + color within each grid.
 */
export const buildPurchaseOrderGroups = (selectedLineIds, linesByOrder, products = []) => {
  const productByStyle = new Map(products.map((p) => [p.styleNo, p]));
  const selectedSet = new Set(selectedLineIds);
  const linesByType = {};

  Object.values(linesByOrder)
    .flat()
    .filter((line) => selectedSet.has(line.id))
    .forEach((line) => {
      const gridType = detectSizeGrid(line.sizes);
      if (!linesByType[gridType]) linesByType[gridType] = [];
      linesByType[gridType].push(line);
    });

  return SIZE_GRID_ORDER.filter((gridType) => linesByType[gridType]?.length)
    .map((gridType) => {
      const lines = linesByType[gridType];
      const sizeColumns = SIZE_GRID_COLUMNS[gridType];
      const aggregatedMap = {};

      lines.forEach((line) => {
        const styleNo = extractStyleNo(line.garmentId);
        const color = (line.garmentColor || '').toUpperCase();
        const key = `${styleNo}|||${color}`;

        if (!aggregatedMap[key]) {
          const sizesNumeric = {};
          Object.entries(line.sizes || {}).forEach(([size, qty]) => {
            sizesNumeric[size] = toQuantity(qty);
          });
          const matchedProduct = productByStyle.get(styleNo) || null;
          aggregatedMap[key] = {
            styleNo,
            garmentColor: line.garmentColor,
            garmentId: line.garmentId,
            sizes: sizesNumeric,
            lineIds: [line.id],
            supplierUrl: matchedProduct?.supplierUrl || null,
            productId: matchedProduct?.id || null,
            product: matchedProduct,
          };
        } else {
          aggregatedMap[key].lineIds.push(line.id);
          Object.entries(line.sizes || {}).forEach(([size, qty]) => {
            const currentQty = toQuantity(aggregatedMap[key].sizes[size]);
            aggregatedMap[key].sizes[size] = currentQty + toQuantity(qty);
          });
        }
      });

      const rows = Object.values(aggregatedMap).sort((a, b) => {
        const styleCmp = a.styleNo.toUpperCase().localeCompare(b.styleNo.toUpperCase());
        if (styleCmp !== 0) return styleCmp;
        return (a.garmentColor || '').toUpperCase().localeCompare((b.garmentColor || '').toUpperCase());
      });

      return { gridType, sizeColumns, rows };
    });
};
